import { useMemo } from 'react'
import {
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoSource,
  GeckoVisualInspection,
} from 'types'

/**
 * Hook to count the number of scans from markup layers
 *
 * For most inspection types: looks for "Scan Labels" markup layer in the "Report" path
 * For phased-array (PAUT): looks for "Weld Scan Labels" markup layer in the "Report" path
 *
 * Both count the number of label primitives within the layer.
 *
 * @returns Map of inspection ID to scan count
 */
export const useScanCount = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  inspections: readonly (GeckoInspection | GeckoVisualInspection)[]
): ReadonlyMap<string, number> => {
  const inspectionTypeById = useMemo(() => {
    const map = new Map<string, string | undefined>()
    inspections.forEach((insp) => {
      map.set(insp.id, insp.data.inspection_type)
    })
    return map
  }, [inspections])

  return useMemo(() => {
    const scanCountByInspection = new Map<string, number>()

    for (const [inspectionId, markupLayers] of markupLayersByInspection) {
      const inspectionType = inspectionTypeById.get(inspectionId)
      const isPAUT = inspectionType === GeckoSource.PhasedArray.id

      const layerName = isPAUT ? 'Weld Scan Labels' : 'Scan Labels'

      const scanLabelsLayers = markupLayers.filter(
        (layer) =>
          layer.data?.name === layerName &&
          Array.isArray(layer.data?.path) &&
          layer.data?.path[0] === 'Report'
      )

      if (scanLabelsLayers.length === 0) {
        scanCountByInspection.set(inspectionId, 0)
        continue
      }

      const scanCount = scanLabelsLayers
        .map((scanLabelsLayer) => scanLabelsLayer.data?.labelPrimitives.length)
        .filter((labelLength) => !!labelLength)
        .reduce((prev, curr) => prev + curr, 0)

      scanCountByInspection.set(inspectionId, scanCount)
    }

    return scanCountByInspection
  }, [markupLayersByInspection, inspectionTypeById])
}
